import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CalendarDays, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch {
      toast.error(t("auth_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#020617] px-4">
      <div className="w-full max-w-md relative z-10">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center">
              <CalendarDays className="w-5 h-5 text-indigo-400" />
            </div>
            <h1 className="text-2xl font-bold text-white font-heading tracking-tight">Book Flowy</h1>
          </div>
          <LanguageSwitcher variant="widget" />
        </div>

        <div className="bg-slate-950/70 backdrop-blur-xl border border-white/10 rounded-xl p-8">
          {!sent ? (
            <>
              <h2 className="text-xl font-semibold text-white font-heading mb-1">{t("forgot_title")}</h2>
              <p className="text-slate-400 text-sm mb-6">{t("forgot_sub")}</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300 text-sm">{t("email")}</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("auth_email_placeholder")}
                    required
                    className="bg-slate-900/50 border-slate-700 focus:border-indigo-500"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-indigo-500 hover:bg-indigo-600 text-white">
                  {loading ? t("loading") : t("forgot_send")}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-2">
              <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center bg-indigo-500/20">
                <span className="text-2xl">📧</span>
              </div>
              <h2 className="text-lg font-bold text-white mb-2">{t("forgot_sent_title")}</h2>
              <p className="text-slate-400 text-sm mb-6">{t("forgot_sent_sub")}</p>
              <Button onClick={() => navigate("/reset-password")} className="w-full bg-indigo-500 hover:bg-indigo-600 text-white">
                {t("forgot_enter_code")}
              </Button>
            </div>
          )}

          <div className="mt-6 text-center">
            <button onClick={() => navigate("/login")} className="text-sm text-slate-400 hover:text-indigo-400 transition-colors flex items-center gap-1 mx-auto">
              <ArrowLeft size={14} /> {t("forgot_back_login")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
